import { useEffect } from "react";
import { useI18n } from "@/lib/i18n";

interface SEOProps {
    title?: string;
    description?: string;
    titleKey?: string;
    descriptionKey?: string;
    dynamicValues?: Record<string, any>;
    isRawTitle?: boolean;
}

/**
 * Hook to manage SEO tags (title and meta description)
 */
export function useSEO({
    title,
    description,
    titleKey,
    descriptionKey,
    dynamicValues,
    isRawTitle = false
}: SEOProps) {
    const { t } = useI18n();

    useEffect(() => {
        // 1. Handle Page Title
        let finalTitle = "";
        if (titleKey) {
            finalTitle = t(titleKey, dynamicValues);
        } else if (title) {
            finalTitle = title;
        }

        if (finalTitle && !isRawTitle) {
            if (!finalTitle.includes("| VintMap")) {
                finalTitle = `${finalTitle} | VintMap`;
            }
        } else if (!finalTitle) {
            finalTitle = "VintMap";
        }

        if (document.title !== finalTitle) {
            document.title = finalTitle;
        }

        // 2. Handle Meta Description
        let finalDescription = "";
        if (descriptionKey) {
            finalDescription = t(descriptionKey, dynamicValues);
        } else if (description) {
            finalDescription = description;
        }

        if (finalDescription) {
            let metaDesc = document.querySelector('meta[name="description"]');
            if (!metaDesc) {
                metaDesc = document.createElement('meta');
                metaDesc.setAttribute('name', 'description');
                document.head.appendChild(metaDesc);
            }
            metaDesc.setAttribute('content', finalDescription);

            // Also update Open Graph description for better social sharing
            let ogDesc = document.querySelector('meta[property="og:description"]');
            if (!ogDesc) {
                ogDesc = document.createElement('meta');
                ogDesc.setAttribute('property', 'og:description');
                document.head.appendChild(ogDesc);
            }
            ogDesc.setAttribute('content', finalDescription);
        }

        // 3. Handle Open Graph Title
        let ogTitle = document.querySelector('meta[property="og:title"]');
        if (!ogTitle) {
            ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            document.head.appendChild(ogTitle);
        }
        ogTitle.setAttribute('content', finalTitle);

    }, [t, title, description, titleKey, descriptionKey, JSON.stringify(dynamicValues), isRawTitle]);
}
